import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { Icon } from '@/components/icons';
import {
  C1Dashboard,
  C1Donut,
  C1Panel,
  C1StatusList,
  C1Table,
  C1Tile,
  type C1StatusRow,
} from '@/components/c1';
import { CategoryBars, SERIES, SplitBar, ThresholdMeter, TrendLine, type TrendPoint } from './charts';
import { formatCount, formatMoney } from '@/utils/format';
import { useEduCoreLaunchUrl } from '@/hooks/useEduCoreLaunchUrl';
import { useInstitutionTerms } from '@/hooks/useInstitutionTerms';
import { EduCorePaths, type EduCorePath } from '@/lib/educoreLinks';
import { attentionPathFor, type InsightsScope } from '@/lib/educorePanelLinks';
import type {
  AttendanceSummary,
  BursarInsights,
  CounsellorInsights,
  ExamOfficerInsights,
  GradeSummary,
  LibrarianInsights,
  ParentInsights,
  RegistrarInsights,
  StudentInsights,
  TeacherInsights,
  TransportInsights,
} from './types';

/**
 * One panel per role, each answering the questions that role actually acts on.
 *
 * The shared rule across all of them: a figure earns its place only if it changes what
 * somebody does this week. Counts that cannot be acted on are left out, however easy they
 * would have been to compute.
 */

type AttentionItem = {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  count: number;
  amount?: number;
};

function attentionTone(
  severity: AttentionItem['severity'],
): C1StatusRow['tone'] {
  if (severity === 'critical') return 'critical';
  if (severity === 'warning') return 'warning';
  return 'neutral';
}

function mapAttentionRows(
  items: AttentionItem[],
  scope: InsightsScope,
  open: (path: EduCorePath) => string | undefined,
  label: (item: AttentionItem) => string,
  value?: (item: AttentionItem) => ReactNode,
): C1StatusRow[] {
  return items.map((item) => {
    const path = attentionPathFor(scope, item.id);
    return {
      id: item.id,
      tone: attentionTone(item.severity),
      label: label(item),
      value: value ? value(item) : formatCount(item.count, 'en'),
      href: path ? open(path) : undefined,
    };
  });
}

/** Attendance days arrive as ISO dates; the axis wants something a person can read. */
function toTrendPoints(series: AttendanceSummary['series'], locale: string): TrendPoint[] {
  return series.map((entry) => ({
    label: new Date(entry.day).toLocaleDateString(locale, { day: 'numeric', month: 'short' }),
    value: entry.rate,
  }));
}

function gradeTrendPoints(series: GradeSummary['series'], locale: string): TrendPoint[] {
  return series.map((entry) => ({
    label: new Date(entry.at).toLocaleDateString(locale, { day: 'numeric', month: 'short' }),
    value: entry.percentage,
  }));
}

/**
 * TEACHER — the work in front of them, and the pupils in trouble.
 *
 * THE AT-RISK LIST IS THE POINT OF THIS PANEL. A class attendance average is a comfortable
 * number that conceals exactly the pupils it should surface: twenty children at 95% and two
 * at 40% average out to a healthy-looking 90%. Naming the individuals is the only form of
 * this statistic a teacher can act on, so it is listed by name and sorted worst-first.
 *
 * The grade distribution sits beside it deliberately. Read together, a class-wide clustering
 * at the low end is a signal about the TEACHING — a topic that did not land — rather than
 * about the pupils, and that reframing is the most useful thing this panel does.
 */
export function TeacherPanel({
  data,
  locale,
  institutionId,
}: {
  data: TeacherInsights;
  locale: string;
  institutionId?: string;
}) {
  const { t } = useTranslation();
  const educore = useEduCoreLaunchUrl(institutionId);
  const { vocab } = useInstitutionTerms(institutionId);
  const open = (path: EduCorePath) => educore.href(path) ?? undefined;
  const today = data.today;
  const attention = data.attention ?? [];

  const attentionItems = mapAttentionRows(
    attention,
    'TEACHER',
    open,
    (item) => t(`insights.teacher.attention.${item.id}`, { count: item.count, ...vocab }),
    (item) => formatCount(item.count, locale),
  );

  const aside =
    attentionItems.length > 0 || data.atRisk.length > 0 ? (
      <>
        {attentionItems.length > 0 && (
          <C1Panel title={t('insights.teacher.attentionTitle')}>
            <C1StatusList items={attentionItems} />
          </C1Panel>
        )}
        {data.atRisk.length > 0 && (
          <C1Panel
            title={t('insights.teacher.atRiskTitle', {
              threshold: data.attendance.threshold,
              ...vocab,
            })}
            action={
              open(EduCorePaths.teacherAttendance) ? (
                <a
                  href={open(EduCorePaths.teacherAttendance)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="c1-tableToggle"
                >
                  {t('insights.openInEducore')}
                </a>
              ) : undefined
            }
          >
            <C1StatusList
              scroll
              items={data.atRisk.map((student) => ({
                id: student.studentId,
                icon: <Icon name="warning" size={14} />,
                label: student.name,
                value: `${student.attendanceRate}%`,
                tone: 'critical' as const,
              }))}
            />
            {data.atRiskTotal > data.atRisk.length && (
              <p className="insight-scopeNote">
                {t('insights.teacher.andMore', { count: data.atRiskTotal - data.atRisk.length })}
              </p>
            )}
          </C1Panel>
        )}
      </>
    ) : null;

  return (
    <C1Dashboard aside={aside} asideLabel={t('insights.teacher.asideLabel')} stickyAside>
      <p className="insight-scopeNote">
        {educore.launchUrl
          ? t('insights.clickToOpen')
          : t('insights.educoreNotLinked')}
      </p>

      <div className="insight-tiles">
        <C1Tile
          label={t('insights.teacher.students')}
          value={formatCount(data.classes.students, locale)}
          icon={<Icon name="users" size={16} />}
          href={open(EduCorePaths.teacherHome)}
        />
        <C1Tile
          label={t('insights.teacher.classes')}
          value={formatCount(data.classes.total, locale)}
          sub={
            today
              ? t('insights.teacher.todayClasses', { count: today.classes })
              : undefined
          }
          icon={<Icon name="campus" size={16} />}
          href={open(EduCorePaths.teacherTimetable)}
        />
        <C1Tile
          label={t('insights.teacher.toMark')}
          value={formatCount(data.marking.submissionsWaiting, locale)}
          sub={t('insights.teacher.overdueCount', { count: data.marking.overdue })}
          icon={<Icon name="checklist" size={16} />}
          tone={data.marking.overdue > 0 ? 'warning' : undefined}
          href={open(EduCorePaths.teacherAssignments)}
        />
        <C1Tile
          label={t('insights.teacher.atRisk')}
          value={formatCount(data.atRiskTotal, locale)}
          icon={<Icon name="warning" size={16} />}
          tone={data.atRiskTotal > 0 ? 'warning' : undefined}
          href={open(EduCorePaths.teacherAttendance)}
        />
        {today && today.pendingRegisters > 0 && (
          <C1Tile
            label={t('insights.teacher.pendingRegisters')}
            value={formatCount(today.pendingRegisters, locale)}
            icon={<Icon name="clock" size={16} />}
            tone="warning"
            href={open(EduCorePaths.teacherAttendance)}
          />
        )}
      </div>

      <div className="insight-charts">
        <TrendLine
          title={t('insights.teacher.attendanceTrend')}
          points={toTrendPoints(data.attendance.series, locale)}
          threshold={data.attendance.threshold}
          thresholdLabel={t('insights.thresholdShort', { threshold: data.attendance.threshold })}
        />
        <C1Donut
          title={t('insights.gradeDistribution')}
          slices={[...data.grades.distribution]
            .sort((a, b) => a.letter.localeCompare(b.letter))
            .map((entry) => ({ key: entry.letter, label: entry.letter, value: entry.count }))}
          format={(n) => formatCount(n, locale)}
          centre={{
            value: formatCount(data.grades.assessed, locale),
            label: t('insights.overview.assessed'),
          }}
        />
      </div>
    </C1Dashboard>
  );
}

/**
 * STUDENT — where they stand, and what is coming.
 *
 * Attendance leads because it is the figure with a consequence attached: below the threshold
 * a pupil cannot sit examinations, so it is shown against that line rather than as a bare
 * percentage. The marks trend answers "am I improving?", which drives self-regulation far
 * better than a current average does — a pupil at 55% climbing from 40% is in a completely
 * different position from one falling from 70%, and a single average hides both.
 */
export function StudentPanel({
  data,
  locale,
  institutionId,
}: {
  data: StudentInsights;
  locale: string;
  institutionId?: string;
}) {
  const { t } = useTranslation();
  const educore = useEduCoreLaunchUrl(institutionId);
  const open = (path: EduCorePath) => educore.href(path) ?? undefined;

  const money = (value: number) => {
    const formatted = formatMoney(value, data.fees.currency, locale);
    return <span title={formatted.exact}>{formatted.display}</span>;
  };

  const attention = data.attention ?? [];
  const attentionItems = mapAttentionRows(
    attention,
    'STUDENT',
    open,
    (item) => t(`insights.student.attention.${item.id}`, { count: item.count }),
    (item) => formatCount(item.count, locale),
  );

  const aside =
    attentionItems.length > 0 || data.upcoming.assignments.length > 0 ? (
      <>
        {attentionItems.length > 0 && (
          <C1Panel title={t('insights.student.attentionTitle')}>
            <C1StatusList items={attentionItems} />
          </C1Panel>
        )}
        {data.upcoming.assignments.length > 0 && (
          <C1Panel
            title={t('insights.student.upcoming')}
            action={
              open(EduCorePaths.studentHomework) ? (
                <a
                  href={open(EduCorePaths.studentHomework)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="c1-tableToggle"
                >
                  {t('insights.openInEducore')}
                </a>
              ) : undefined
            }
          >
            <C1StatusList
              items={data.upcoming.assignments.slice(0, 5).map((item) => ({
                id: item.id,
                icon: <Icon name="clock" size={14} />,
                label: item.title,
                value: item.dueDate
                  ? new Date(item.dueDate).toLocaleDateString(locale, {
                      day: 'numeric',
                      month: 'short',
                    })
                  : '—',
                href: open(EduCorePaths.studentHomework),
              }))}
            />
          </C1Panel>
        )}
      </>
    ) : null;

  return (
    <C1Dashboard aside={aside} asideLabel={t('insights.student.asideLabel')} stickyAside>
      <p className="insight-scopeNote">
        {educore.launchUrl ? t('insights.clickToOpen') : t('insights.educoreNotLinked')}
      </p>

      <div className="insight-charts">
        <ThresholdMeter
          title={t('insights.student.attendance')}
          value={data.attendance.rate}
          threshold={data.attendance.threshold}
          thresholdLabel={t('insights.thresholdShort', { threshold: data.attendance.threshold })}
          caption={t('insights.student.absences', { count: data.attendance.absences })}
        />
        <TrendLine
          title={t('insights.student.marksTrend')}
          points={gradeTrendPoints(data.grades.series, locale)}
          color={SERIES[1]}
        />
      </div>

      <div className="insight-tiles">
        <C1Tile
          label={t('insights.student.average')}
          value={data.grades.average ?? '—'}
          sub={data.grades.average !== null ? '%' : undefined}
          icon={<Icon name="academic" size={16} />}
          href={open(EduCorePaths.studentProgress)}
        />
        <C1Tile
          label={t('insights.student.assessed')}
          value={formatCount(data.grades.assessed, locale)}
          icon={<Icon name="report" size={16} />}
          href={open(EduCorePaths.studentProgress)}
        />
        <C1Tile
          label={t('insights.student.dueSoon')}
          value={formatCount(data.upcoming.assignments.length, locale)}
          icon={<Icon name="checklist" size={16} />}
          href={open(EduCorePaths.studentHomework)}
        />
        <C1Tile
          label={t('insights.student.balance')}
          value={money(data.fees.outstanding)}
          sub={
            data.fees.settled
              ? t('insights.student.feesCleared')
              : t('insights.student.feesNotCleared')
          }
          icon={<Icon name="report" size={16} />}
          tone={data.fees.outstanding > 0 ? 'warning' : undefined}
          href={open(EduCorePaths.studentFees)}
        />
      </div>
    </C1Dashboard>
  );
}

/**
 * PARENT — one card per child, never a blended figure.
 *
 * `parent.children` is an array, and averaging across it is the failure that matters here:
 * a parent with three children told "attendance 84%" learns nothing about any of them, and
 * the child in difficulty is precisely the one the average conceals. Each child gets their
 * own attendance meter against the same threshold, their own average, and their own balance.
 */
export function ParentPanel({
  data,
  locale,
  institutionId,
}: {
  data: ParentInsights;
  locale: string;
  institutionId?: string;
}) {
  const { t } = useTranslation();
  const educore = useEduCoreLaunchUrl(institutionId);
  const open = (path: EduCorePath) => educore.href(path) ?? undefined;

  if (data.children.length === 0) {
    return <p className="insights-unavailable">{t('insights.parent.noChildren')}</p>;
  }

  return (
    <C1Dashboard asideLabel={t('insights.parent.asideLabel')}>
      <p className="insight-scopeNote">
        {educore.launchUrl ? t('insights.clickToOpen') : t('insights.educoreNotLinked')}
      </p>

      <div className="insight-children">
        {data.children.map((child) => {
          const childAttention = child.attention ?? [];
          const attentionItems = mapAttentionRows(
            childAttention,
            'PARENT',
            open,
            (item) => t(`insights.parent.attention.${item.id}`, { count: item.count }),
            (item) => formatCount(item.count, locale),
          );
          const money = formatMoney(child.outstandingFees, child.currency, locale);

          return (
            <section key={child.id} className="insight-childCard">
              <header className="insight-childHeader">
                <span className="insight-childName">{child.name}</span>
                {child.className && <span className="insight-childClass">{child.className}</span>}
                {child.needsAttention && (
                  <span className="insight-childFlag">
                    <Icon name="warning" size={12} aria-hidden="true" />
                    {t('insights.parent.needsAttention')}
                  </span>
                )}
              </header>

              <div className="insight-tiles">
                <C1Tile
                  label={t('insights.parent.attendance')}
                  value={child.attendance.rate === null ? '—' : `${child.attendance.rate}%`}
                  sub={t('insights.thresholdShort', { threshold: child.attendance.threshold })}
                  icon={<Icon name="pulse" size={16} />}
                  tone={
                    child.attendance.rate !== null &&
                    child.attendance.rate < child.attendance.threshold
                      ? 'warning'
                      : undefined
                  }
                  href={open(EduCorePaths.parentReports)}
                />
                <C1Tile
                  label={t('insights.parent.average')}
                  value={child.grades.average ?? '—'}
                  sub={child.grades.average !== null ? '%' : undefined}
                  icon={<Icon name="academic" size={16} />}
                  href={open(EduCorePaths.parentReports)}
                />
                <C1Tile
                  label={t('insights.parent.balance')}
                  value={<span title={money.exact}>{money.display}</span>}
                  sub={
                    child.settled
                      ? t('insights.parent.feesCleared')
                      : t('insights.parent.feesNotCleared')
                  }
                  icon={<Icon name="report" size={16} />}
                  tone={child.outstandingFees > 0 ? 'warning' : undefined}
                  href={open(EduCorePaths.parentFees)}
                />
              </div>

              <ThresholdMeter
                title={t('insights.parent.attendance')}
                value={child.attendance.rate}
                threshold={child.attendance.threshold}
                thresholdLabel={t('insights.thresholdShort', {
                  threshold: child.attendance.threshold,
                })}
                caption={t('insights.student.absences', { count: child.attendance.absences })}
              />

              {attentionItems.length > 0 && (
                <C1Panel title={t('insights.parent.attentionTitle')}>
                  <C1StatusList items={attentionItems} />
                </C1Panel>
              )}
            </section>
          );
        })}
      </div>
    </C1Dashboard>
  );
}

/**
 * BURSAR — a finance command centre, not a report.
 *
 * Value + context + exception. A lone collection rate does not tell Cécile whether
 * she is on track; the share of billed that is still out, the movement against last
 * month, and the list of things that need her this week do. Academic figures stay
 * off this panel — a bursar's students are the ones with balances.
 *
 * EVERY TILE AND ATTENTION ROW THAT CAN ACT OPENS EDUCORE. CampusOne has no
 * Collections / Outstanding routes; the product launchUrl plus an EduCore SPA path
 * is the honest destination. When EduCore is not entitled, the tiles stay inert
 * rather than linking to a 404 on this host.
 *
 * EVERY EXTENDED REGION IS GUARDED. EduCore and this console deploy separately; an
 * older EduCore omits ageing, attention and by-unit, and the panel falls back to
 * the four tiles and two charts it has always had rather than taking the home page
 * down.
 */
export function BursarPanel({
  data,
  locale,
  institutionId,
}: {
  data: BursarInsights;
  locale: string;
  institutionId?: string;
}) {
  const { t } = useTranslation();
  const educore = useEduCoreLaunchUrl(institutionId);
  const open = (path: (typeof EduCorePaths)[keyof typeof EduCorePaths]) =>
    educore.href(path) ?? undefined;

  const money = (value: number) => {
    const formatted = formatMoney(value, data.currency, locale);
    return <span title={formatted.exact}>{formatted.display}</span>;
  };
  const moneyText = (value: number) => formatMoney(value, data.currency, locale).display;

  const billStatusLabel = (status: string) => {
    const key = `insights.bursar.billStatus.${status}`;
    const translated = t(key);
    return translated === key ? status : translated;
  };

  const settlementLabel = (status: string) => {
    const key = `insights.bursar.settlementStatus.${status}`;
    const translated = t(key);
    return translated === key ? status : translated;
  };

  const statusRows = (
    data.billsByStatus ?? data.statusMix.map((row) => ({ ...row, outstanding: 0 }))
  ).map((entry) => ({
    key: entry.status,
    label: billStatusLabel(entry.status),
    value: entry.count,
  }));

  const attention = data.attention ?? [];
  const ageing = data.ageing;
  const period = data.period;
  const byUnit = data.byUnit ?? [];
  const monthly = data.monthly ?? [];
  const settlements = data.settlementsByStatus ?? [];
  const expensesByCategory = data.expensesByCategory ?? [];
  const topDebtors = data.topDebtors ?? [];
  const byYear = data.byYear ?? [];

  const attentionItems: C1StatusRow[] = attention.map((item) => ({
    id: item.id,
    tone:
      item.severity === 'critical'
        ? 'critical'
        : item.severity === 'warning'
          ? 'warning'
          : 'neutral',
    label: t(`insights.bursar.attention.${item.id}`, {
      count: item.count,
      amount: item.amount != null ? moneyText(item.amount) : '',
    }),
    value: item.amount != null ? money(item.amount) : formatCount(item.count, locale),
    href: attentionPathFor('BURSAR', item.id)
      ? open(attentionPathFor('BURSAR', item.id)!)
      : undefined,
  }));

  const ageingDonut =
    ageing?.buckets
      .filter((row) => row.amount > 0)
      .map((row) => ({
        key: row.key,
        label: t(`insights.bursar.ageing.${row.key}`),
        value: row.amount,
      })) ?? [];

  const aside =
    attentionItems.length > 0 || topDebtors.length > 0 || byUnit.length > 0 ? (
      <>
        {attentionItems.length > 0 && (
          <C1Panel title={t('insights.bursar.attentionTitle')}>
            <C1StatusList items={attentionItems} />
          </C1Panel>
        )}

        {topDebtors.length > 0 && (
          <C1Panel
            title={t('insights.bursar.topDebtorsTitle')}
            action={
              open(EduCorePaths.fees) ? (
                <a
                  href={open(EduCorePaths.fees)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="c1-tableToggle"
                >
                  {t('insights.bursar.openInEducore')}
                </a>
              ) : undefined
            }
          >
            <C1Table
              caption={t('insights.bursar.topDebtorsTitle')}
              scroll
              columns={[
                {
                  key: 'name',
                  header: t('insights.bursar.debtorName'),
                  cell: (row: { name: string }) => row.name,
                  rowHeader: true,
                },
                {
                  key: 'unit',
                  header: t('insights.bursar.byUnitUnit'),
                  cell: (row: { unit: string }) => row.unit,
                },
                {
                  key: 'outstanding',
                  header: t('insights.bursar.outstanding'),
                  align: 'end',
                  cell: (row: { outstanding: number }) => money(row.outstanding),
                },
              ]}
              rows={topDebtors}
              rowKey={(row) => row.studentId || row.name}
            />
          </C1Panel>
        )}

        {byUnit.length > 0 && (
          <C1Panel title={t('insights.bursar.byUnitTitle')}>
            <C1Table
              caption={t('insights.bursar.byUnitTitle')}
              scroll
              columns={[
                {
                  key: 'unit',
                  header: t('insights.bursar.byUnitUnit'),
                  cell: (row: { unit: string }) => row.unit,
                  rowHeader: true,
                },
                {
                  key: 'rate',
                  header: t('insights.bursar.collectionRate'),
                  align: 'end',
                  cell: (row: { collectionRate: number | null }) =>
                    row.collectionRate === null ? '—' : `${row.collectionRate}%`,
                },
                {
                  key: 'outstanding',
                  header: t('insights.bursar.outstanding'),
                  align: 'end',
                  cell: (row: { outstanding: number }) => money(row.outstanding),
                },
              ]}
              rows={byUnit.slice(0, 8)}
              rowKey={(row) => row.unit}
            />
          </C1Panel>
        )}
      </>
    ) : null;

  return (
    <C1Dashboard aside={aside} asideLabel={t('insights.bursar.asideLabel')} stickyAside>
      {data.computedAt && (
        <p className="insight-scopeNote">
          {t('insights.bursar.asOf', {
            when: new Date(data.computedAt).toLocaleString(locale, {
              dateStyle: 'medium',
              timeStyle: 'short',
            }),
          })}
          {educore.launchUrl
            ? ` · ${t('insights.bursar.clickToOpen')}`
            : ` · ${t('insights.bursar.educoreUnavailable')}`}
        </p>
      )}

      <div className="insight-tiles">
        <C1Tile
          label={t('insights.bursar.collectionRate')}
          value={data.collectionRate === null ? '—' : `${data.collectionRate}%`}
          sub={
            data.outstandingShare != null
              ? t('insights.bursar.outstandingShare', { share: data.outstandingShare })
              : undefined
          }
          icon={<Icon name="report" size={16} />}
          href={open(EduCorePaths.reports)}
          trend={
            period
              ? {
                  delta: period.rateDeltaPts,
                  unit: t('insights.bursar.pts'),
                  comparison: t('insights.bursar.vsLastMonth'),
                  goodWhen: 'up',
                }
              : undefined
          }
        />
        <C1Tile
          label={t('insights.bursar.billed')}
          value={money(data.billed)}
          icon={<Icon name="checklist" size={16} />}
          href={open(EduCorePaths.fees)}
        />
        <C1Tile
          label={t('insights.bursar.collected')}
          value={money(data.collected)}
          sub={
            period
              ? t('insights.bursar.collectedThisMonth', {
                  amount: moneyText(period.collectedThisMonth),
                })
              : undefined
          }
          icon={<Icon name="check" size={16} />}
          href={open(EduCorePaths.transactions)}
          sparkline={monthly.map((row) => row.collected)}
        />
        <C1Tile
          label={t('insights.bursar.outstanding')}
          value={money(data.outstanding)}
          sub={
            ageing?.overdue && ageing.overdue.amount > 0
              ? t('insights.bursar.overdueSub', {
                  amount: moneyText(ageing.overdue.amount),
                })
              : data.outstandingShare != null
                ? t('insights.bursar.ofBilled', { share: data.outstandingShare })
                : undefined
          }
          icon={<Icon name="warning" size={16} />}
          tone={data.outstanding > 0 ? 'warning' : undefined}
          href={open(EduCorePaths.fees)}
        />

        {data.debtors && (
          <C1Tile
            label={t('insights.bursar.debtors')}
            value={formatCount(data.debtors.count, locale)}
            sub={
              data.debtors.average != null
                ? t('insights.bursar.averageDebt', {
                    amount: moneyText(data.debtors.average),
                  })
                : undefined
            }
            icon={<Icon name="users" size={16} />}
            href={open(EduCorePaths.fees)}
          />
        )}
        {data.dueThisWeek && (
          <C1Tile
            label={t('insights.bursar.dueThisWeek')}
            value={money(data.dueThisWeek.amount)}
            sub={t('insights.bursar.dueThisWeekCount', { count: data.dueThisWeek.count })}
            icon={<Icon name="clock" size={16} />}
            tone={data.dueThisWeek.count > 0 ? 'warning' : undefined}
            href={open(EduCorePaths.fees)}
          />
        )}
        {data.partial && data.partial.count > 0 && (
          <C1Tile
            label={t('insights.bursar.partial')}
            value={formatCount(data.partial.count, locale)}
            sub={money(data.partial.outstanding)}
            icon={<Icon name="pulse" size={16} />}
            href={open(EduCorePaths.fees)}
          />
        )}
        {period?.expensesThisMonth != null && (
          <C1Tile
            label={t('insights.bursar.expensesThisMonth')}
            value={money(period.expensesThisMonth)}
            sub={
              period.netThisMonth != null
                ? t('insights.bursar.netThisMonth', {
                    amount: moneyText(period.netThisMonth),
                  })
                : undefined
            }
            icon={<Icon name="report" size={16} />}
            href={open(EduCorePaths.expenses)}
          />
        )}
        {data.settlementPressure && data.settlementPressure.failedCount > 0 && (
          <C1Tile
            label={t('insights.bursar.failedSettlements')}
            value={formatCount(data.settlementPressure.failedCount, locale)}
            sub={money(data.settlementPressure.failedAmount)}
            icon={<Icon name="error" size={16} />}
            tone="critical"
            href={open(EduCorePaths.transactions)}
          />
        )}
        {data.settlementPressure && data.settlementPressure.pendingCount > 0 && (
          <C1Tile
            label={t('insights.bursar.pendingSettlements')}
            value={formatCount(data.settlementPressure.pendingCount, locale)}
            sub={money(data.settlementPressure.pendingAmount)}
            icon={<Icon name="clock" size={16} />}
            tone="warning"
            href={open(EduCorePaths.reconciliation)}
          />
        )}
      </div>

      <div className="insight-charts">
        <SplitBar
          title={t('insights.bursar.split')}
          format={moneyText}
          parts={[
            {
              key: 'collected',
              label: t('insights.bursar.collected'),
              value: data.collected,
              color: SERIES[0],
            },
            {
              key: 'outstanding',
              label: t('insights.bursar.outstanding'),
              value: data.outstanding,
              color: SERIES[2],
            },
          ]}
        />
        <C1Donut
          title={t('insights.bursar.billsByStatus')}
          slices={statusRows}
          format={(value) => formatCount(value, locale)}
          centre={{
            value: formatCount(data.bills ?? data.accounts, locale),
            label: t('insights.bursar.bills'),
          }}
          emptyMessage={t('insights.bursar.noBills')}
        />
        {ageingDonut.length > 0 && (
          <C1Donut
            title={t('insights.bursar.ageingTitle')}
            slices={ageingDonut}
            format={moneyText}
            centre={{
              value: money(ageing!.overdue.amount),
              label: t('insights.bursar.overdueLabel'),
            }}
          />
        )}
        {settlements.length > 0 && (
          <C1Donut
            title={t('insights.bursar.settlementsThisMonth')}
            slices={settlements.map((row) => ({
              key: row.status,
              label: settlementLabel(row.status),
              value: row.count,
            }))}
            format={(value) => formatCount(value, locale)}
            centre={{
              value: formatCount(
                settlements.reduce((sum, row) => sum + row.count, 0),
                locale,
              ),
              label: t('insights.bursar.settlements'),
            }}
          />
        )}
        {expensesByCategory.length > 0 && (
          <C1Donut
            title={t('insights.bursar.expensesByCategory')}
            slices={expensesByCategory.map((row) => ({
              key: row.category,
              label: row.category,
              value: row.amount,
            }))}
            format={moneyText}
            centre={{
              value: money(period?.expensesThisMonth ?? 0),
              label: t('insights.bursar.expensesThisMonth'),
            }}
          />
        )}
        {byYear.length > 0 && (
          <CategoryBars
            title={t('insights.bursar.byYear')}
            data={byYear.map((row) => ({
              key: row.year,
              label: row.year,
              value: row.collected,
            }))}
            emptyMessage={t('insights.bursar.noBills')}
          />
        )}
        {byUnit.length > 0 && (
          <CategoryBars
            title={t('insights.bursar.outstandingByUnit')}
            data={byUnit.slice(0, 8).map((row) => ({
              key: row.unit,
              label: row.unit,
              value: row.outstanding,
            }))}
            color={SERIES[2]}
          />
        )}
        {monthly.length > 0 && (
          <TrendLine
            title={t('insights.bursar.collectionTrend')}
            points={monthly.map((row) => ({
              label: row.month,
              value: Math.round((row.collected / 1_000_000) * 10) / 10,
            }))}
            unit="M"
          />
        )}
      </div>

      {ageing && ageing.undated.count > 0 && (
        <p className="insight-scopeNote">
          {t('insights.bursar.ageingUndated', {
            count: ageing.undated.count,
            amount: moneyText(ageing.undated.amount),
          })}
        </p>
      )}
    </C1Dashboard>
  );
}

/**
 * LIBRARIAN — circulation, led by what is overdue.
 *
 * Books on loan is an inert number: a library is meant to lend books. The subset past its
 * return date and still out is the only part anyone acts on, so it leads and the items are
 * named.
 */
export function LibrarianPanel({
  data,
  locale,
  institutionId,
}: {
  data: LibrarianInsights;
  locale: string;
  institutionId?: string;
}) {
  const { t } = useTranslation();
  const educore = useEduCoreLaunchUrl(institutionId);
  const open = (path: EduCorePath) => educore.href(path) ?? undefined;
  const attention = data.attention ?? [];
  const attentionItems = mapAttentionRows(
    attention,
    'LIBRARIAN',
    open,
    (item) => t(`insights.librarian.attention.${item.id}`, { count: item.count }),
    (item) => formatCount(item.count, locale),
  );

  const aside =
    attentionItems.length > 0 || data.overdueItems.length > 0 ? (
      <>
        {attentionItems.length > 0 && (
          <C1Panel title={t('insights.librarian.attentionTitle')}>
            <C1StatusList items={attentionItems} />
          </C1Panel>
        )}
        {data.overdueItems.length > 0 && (
          <C1Panel
            title={t('insights.librarian.overdueList')}
            action={
              open(EduCorePaths.library) ? (
                <a
                  href={open(EduCorePaths.library)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="c1-tableToggle"
                >
                  {t('insights.openInEducore')}
                </a>
              ) : undefined
            }
          >
            <C1StatusList
              scroll={data.overdueItems.length > 6}
              items={data.overdueItems.map((item) => ({
                id: item.id,
                icon: <Icon name="warning" size={14} />,
                label: item.title,
                value: item.dueDate
                  ? new Date(item.dueDate).toLocaleDateString(locale, {
                      day: 'numeric',
                      month: 'short',
                    })
                  : '—',
                tone: 'critical' as const,
                href: open(EduCorePaths.library),
              }))}
            />
            {data.overdue > data.overdueItems.length && (
              <p className="insight-scopeNote">
                {t('insights.teacher.andMore', { count: data.overdue - data.overdueItems.length })}
              </p>
            )}
          </C1Panel>
        )}
      </>
    ) : null;

  return (
    <C1Dashboard aside={aside} asideLabel={t('insights.librarian.asideLabel')} stickyAside>
      <p className="insight-scopeNote">
        {educore.launchUrl ? t('insights.clickToOpen') : t('insights.educoreNotLinked')}
      </p>
      <div className="insight-tiles">
        <C1Tile
          label={t('insights.librarian.overdue')}
          value={formatCount(data.overdue, locale)}
          icon={<Icon name="warning" size={16} />}
          tone={data.overdue > 0 ? 'warning' : undefined}
          href={open(EduCorePaths.library)}
        />
        <C1Tile
          label={t('insights.librarian.onLoan')}
          value={formatCount(data.onLoan, locale)}
          icon={<Icon name="checklist" size={16} />}
          href={open(EduCorePaths.library)}
        />
        <C1Tile
          label={t('insights.librarian.returned')}
          value={formatCount(data.returned, locale)}
          icon={<Icon name="check" size={16} />}
          href={open(EduCorePaths.librarianOverview)}
        />
        <C1Tile
          label={t('insights.librarian.totalLoans')}
          value={formatCount(data.totalLoans, locale)}
          icon={<Icon name="report" size={16} />}
          href={open(EduCorePaths.inventory)}
        />
      </div>

      <div className="insight-charts">
        <C1Donut
          title={t('insights.librarian.circulation')}
          slices={[
            { key: 'onLoan', label: t('insights.librarian.onLoan'), value: data.onLoan },
            { key: 'returned', label: t('insights.librarian.returned'), value: data.returned },
            { key: 'overdue', label: t('insights.librarian.overdue'), value: data.overdue },
          ].filter((s) => s.value > 0)}
          format={(n) => formatCount(n, locale)}
          centre={{
            value: formatCount(data.totalLoans, locale),
            label: t('insights.librarian.totalLoans'),
          }}
        />
        <CategoryBars
          title={t('insights.librarian.popular')}
          data={data.popular.map((entry) => ({
            key: entry.title,
            label: entry.title,
            value: entry.count,
          }))}
          emptyMessage={t('insights.librarian.noLoans')}
        />
      </div>
    </C1Dashboard>
  );
}

/**
 * REGISTRAR and ADMISSIONS OFFICER — who is on the roll, and who is arriving.
 *
 * Intake genuinely is a time series: admissions carry an enrollmentDate. The status mix
 * matters as much as the headcount — a roll of 900 with 200 suspended is a different
 * institution from one with 900 active, and only the breakdown shows it.
 */
export function RegistrarPanel({
  data,
  locale,
  institutionId,
}: {
  data: RegistrarInsights;
  locale: string;
  institutionId?: string;
}) {
  const { t } = useTranslation();
  const educore = useEduCoreLaunchUrl(institutionId);
  const open = (path: EduCorePath) => educore.href(path) ?? undefined;
  const attention = data.attention ?? [];
  const attentionItems = mapAttentionRows(
    attention,
    'REGISTRAR',
    open,
    (item) => t(`insights.registrar.attention.${item.id}`, { count: item.count }),
    (item) => formatCount(item.count, locale),
  );

  const intakePoints: TrendPoint[] = data.intake.map((entry) => ({
    label: new Date(`${entry.month}-01`).toLocaleDateString(locale, {
      month: 'short',
      year: '2-digit',
    }),
    value: entry.count,
  }));

  const aside =
    attentionItems.length > 0 ? (
      <C1Panel title={t('insights.registrar.attentionTitle')}>
        <C1StatusList items={attentionItems} />
      </C1Panel>
    ) : null;

  return (
    <C1Dashboard aside={aside} asideLabel={t('insights.registrar.asideLabel')} stickyAside>
      <p className="insight-scopeNote">
        {educore.launchUrl ? t('insights.clickToOpen') : t('insights.educoreNotLinked')}
      </p>
      <div className="insight-tiles">
        <C1Tile
          label={t('insights.registrar.active')}
          value={formatCount(data.active, locale)}
          icon={<Icon name="users" size={16} />}
          href={open(EduCorePaths.students)}
        />
        <C1Tile
          label={t('insights.registrar.total')}
          value={formatCount(data.total, locale)}
          icon={<Icon name="institution" size={16} />}
          href={open(EduCorePaths.registrarOverview)}
        />
        <C1Tile
          label={t('insights.registrar.graduated')}
          value={formatCount(data.graduated, locale)}
          icon={<Icon name="academic" size={16} />}
          href={open(EduCorePaths.students)}
        />
        <C1Tile
          label={t('insights.registrar.suspended')}
          value={formatCount(data.suspended, locale)}
          icon={<Icon name="ban" size={16} />}
          tone={data.suspended > 0 ? 'warning' : undefined}
          href={open(EduCorePaths.students)}
        />
        {data.newThisMonth != null && (
          <C1Tile
            label={t('insights.registrar.newThisMonth')}
            value={formatCount(data.newThisMonth, locale)}
            icon={<Icon name="users" size={16} />}
            href={open(EduCorePaths.admissions)}
          />
        )}
      </div>

      <div className="insight-charts">
        <TrendLine
          title={t('insights.registrar.intake')}
          points={intakePoints}
          unit=""
          color={SERIES[1]}
        />
        <C1Donut
          title={t('insights.registrar.statusMix')}
          slices={data.statusMix.map((entry) => ({
            key: entry.status,
            label: entry.status,
            value: entry.count,
          }))}
          format={(n) => formatCount(n, locale)}
          centre={{
            value: formatCount(data.total, locale),
            label: t('insights.registrar.total'),
          }}
        />
        <CategoryBars
          title={t('insights.registrar.byDepartment')}
          data={data.byDepartment.map((entry) => ({
            key: entry.department,
            label: entry.department,
            value: entry.count,
          }))}
          emptyMessage={t('insights.registrar.noDepartments')}
        />
      </div>
    </C1Dashboard>
  );
}

/**
 * EXAMINATION OFFICER — whether the marks are in, and how the papers performed.
 *
 * The pass rate is computed against each paper's own passing mark rather than a flat 50%,
 * and is null when nothing is assessable — "no marks entered" and "everybody failed" are
 * opposite findings that a zero would merge.
 */
export function ExamOfficerPanel({
  data,
  locale,
  institutionId,
}: {
  data: ExamOfficerInsights;
  locale: string;
  institutionId?: string;
}) {
  const { t } = useTranslation();
  const educore = useEduCoreLaunchUrl(institutionId);
  const open = (path: EduCorePath) => educore.href(path) ?? undefined;
  const attention = data.attention ?? [];
  const attentionItems = mapAttentionRows(
    attention,
    'EXAM_OFFICER',
    open,
    (item) => t(`insights.exam.attention.${item.id}`, { count: item.count }),
    (item) => formatCount(item.count, locale),
  );

  const aside =
    attentionItems.length > 0 || data.schedule.length > 0 ? (
      <>
        {attentionItems.length > 0 && (
          <C1Panel title={t('insights.exam.attentionTitle')}>
            <C1StatusList items={attentionItems} />
          </C1Panel>
        )}
        {data.schedule.length > 0 && (
          <C1Panel
            title={t('insights.exam.schedule')}
            action={
              open(EduCorePaths.exams) ? (
                <a
                  href={open(EduCorePaths.exams)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="c1-tableToggle"
                >
                  {t('insights.openInEducore')}
                </a>
              ) : undefined
            }
          >
            <C1StatusList
              scroll={data.schedule.length > 6}
              items={data.schedule.map((exam) => ({
                id: exam.id,
                icon: <Icon name="clock" size={14} />,
                label: exam.title,
                value: exam.date
                  ? new Date(exam.date).toLocaleDateString(locale, {
                      day: 'numeric',
                      month: 'short',
                    })
                  : '—',
                href: open(EduCorePaths.exams),
              }))}
            />
          </C1Panel>
        )}
      </>
    ) : null;

  return (
    <C1Dashboard aside={aside} asideLabel={t('insights.exam.asideLabel')} stickyAside>
      <p className="insight-scopeNote">
        {educore.launchUrl ? t('insights.clickToOpen') : t('insights.educoreNotLinked')}
      </p>
      <div className="insight-tiles">
        <C1Tile
          label={t('insights.exam.passRate')}
          value={data.passRate === null ? '—' : `${data.passRate}%`}
          sub={t('insights.exam.assessedContext', { count: data.assessed })}
          icon={<Icon name="academic" size={16} />}
          href={open(EduCorePaths.grades)}
        />
        <C1Tile
          label={t('insights.exam.upcoming')}
          value={formatCount(data.upcomingExams, locale)}
          icon={<Icon name="clock" size={16} />}
          href={open(EduCorePaths.exams)}
        />
        <C1Tile
          label={t('insights.exam.marksRecorded')}
          value={formatCount(data.marksRecorded, locale)}
          sub={
            data.examsWithMarks != null
              ? t('insights.exam.examsWithMarks', { count: data.examsWithMarks })
              : undefined
          }
          icon={<Icon name="checklist" size={16} />}
          href={open(EduCorePaths.grades)}
        />
        <C1Tile
          label={t('insights.exam.total')}
          value={formatCount(data.totalExams, locale)}
          sub={
            data.marksGap != null && data.marksGap > 0
              ? t('insights.exam.marksGap', { count: data.marksGap })
              : undefined
          }
          icon={<Icon name="report" size={16} />}
          tone={data.marksGap != null && data.marksGap > 0 ? 'warning' : undefined}
          href={open(EduCorePaths.exams)}
        />
      </div>

      <div className="insight-charts">
        <C1Donut
          title={t('insights.gradeDistribution')}
          slices={[...data.grades.distribution]
            .sort((a, b) => a.letter.localeCompare(b.letter))
            .map((entry) => ({ key: entry.letter, label: entry.letter, value: entry.count }))}
          format={(n) => formatCount(n, locale)}
          centre={{
            value: formatCount(data.grades.assessed, locale),
            label: t('insights.overview.assessed'),
          }}
        />
      </div>
    </C1Dashboard>
  );
}

/**
 * COUNSELLOR — the pupils in difficulty, across the whole school.
 *
 * The same at-risk computation a teacher gets, but not bounded to one teacher's classes. A
 * counsellor's work is precisely the children whose difficulty spans subjects, which no
 * single teacher's view can surface.
 */
export function CounsellorPanel({
  data,
  locale,
  institutionId,
}: {
  data: CounsellorInsights;
  locale: string;
  institutionId?: string;
}) {
  const { t } = useTranslation();
  const educore = useEduCoreLaunchUrl(institutionId);
  const { vocab } = useInstitutionTerms(institutionId);
  const open = (path: EduCorePath) => educore.href(path) ?? undefined;
  const threshold = data.threshold ?? data.attendance.threshold;
  const attention = data.attention ?? [];
  const attentionItems = mapAttentionRows(
    attention,
    'COUNSELLOR',
    open,
    (item) => t(`insights.counsellor.attention.${item.id}`, { count: item.count, ...vocab }),
    (item) => formatCount(item.count, locale),
  );

  const aside =
    attentionItems.length > 0 || data.atRisk.length > 0 ? (
      <>
        {attentionItems.length > 0 && (
          <C1Panel title={t('insights.counsellor.attentionTitle')}>
            <C1StatusList items={attentionItems} />
          </C1Panel>
        )}
        {data.atRisk.length > 0 && (
          <C1Panel
            title={t('insights.teacher.atRiskTitle', { threshold })}
            action={
              open(EduCorePaths.students) ? (
                <a
                  href={open(EduCorePaths.students)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="c1-tableToggle"
                >
                  {t('insights.openInEducore')}
                </a>
              ) : undefined
            }
          >
            <C1StatusList
              scroll
              items={data.atRisk.map((student) => ({
                id: student.studentId,
                icon: <Icon name="warning" size={14} />,
                label: student.name,
                value: `${student.attendanceRate}%`,
                tone: 'critical' as const,
                href: open(EduCorePaths.students),
              }))}
            />
            {data.atRiskTotal > data.atRisk.length && (
              <p className="insight-scopeNote">
                {t('insights.teacher.andMore', { count: data.atRiskTotal - data.atRisk.length })}
              </p>
            )}
          </C1Panel>
        )}
      </>
    ) : null;

  return (
    <C1Dashboard aside={aside} asideLabel={t('insights.counsellor.asideLabel')} stickyAside>
      <p className="insight-scopeNote">
        {t('insights.counsellor.thresholdNote', { threshold })}
        {educore.launchUrl
          ? ` · ${t('insights.clickToOpen')}`
          : ` · ${t('insights.educoreNotLinked')}`}
      </p>
      <div className="insight-tiles">
        <C1Tile
          label={t('insights.counsellor.atRisk', vocab)}
          value={formatCount(data.atRiskTotal, locale)}
          sub={t('insights.counsellor.belowThreshold', { threshold })}
          icon={<Icon name="warning" size={16} />}
          tone={data.atRiskTotal > 0 ? 'warning' : undefined}
          href={open(EduCorePaths.students)}
        />
        <C1Tile
          label={t('insights.counsellor.tracked', vocab)}
          value={formatCount(data.studentsTracked, locale)}
          icon={<Icon name="users" size={16} />}
          href={open(EduCorePaths.students)}
        />
        <C1Tile
          label={t('insights.counsellor.attendance', vocab)}
          value={data.attendance.rate === null ? '—' : `${data.attendance.rate}%`}
          icon={<Icon name="pulse" size={16} />}
          href={open(EduCorePaths.attendance)}
        />
        <C1Tile
          label={t('insights.counsellor.absences')}
          value={formatCount(data.attendance.absences, locale)}
          icon={<Icon name="ban" size={16} />}
          href={open(EduCorePaths.attendance)}
        />
      </div>

      <div className="insight-charts">
        <TrendLine
          title={t('insights.counsellor.trend', vocab)}
          points={toTrendPoints(data.attendance.series, locale)}
          threshold={data.attendance.threshold}
          thresholdLabel={t('insights.thresholdShort', { threshold: data.attendance.threshold })}
        />
      </div>
    </C1Dashboard>
  );
}

/** TRANSPORT MANAGER — routes, and which of them are over budget. */
export function TransportPanel({
  data,
  locale,
  institutionId,
}: {
  data: TransportInsights;
  locale: string;
  institutionId?: string;
}) {
  const { t } = useTranslation();
  const educore = useEduCoreLaunchUrl(institutionId);
  const open = (path: EduCorePath) => educore.href(path) ?? undefined;
  const attention = data.attention ?? [];
  const attentionItems = mapAttentionRows(
    attention,
    'TRANSPORT',
    open,
    (item) => t(`insights.transport.attention.${item.id}`, { count: item.count }),
    (item) => formatCount(item.count, locale),
  );

  const aside =
    attentionItems.length > 0 ? (
      <C1Panel title={t('insights.transport.attentionTitle')}>
        <C1StatusList items={attentionItems} />
      </C1Panel>
    ) : null;

  return (
    <C1Dashboard aside={aside} asideLabel={t('insights.transport.asideLabel')} stickyAside>
      <p className="insight-scopeNote">
        {educore.launchUrl ? t('insights.clickToOpen') : t('insights.educoreNotLinked')}
      </p>
      <div className="insight-tiles">
        <C1Tile
          label={t('insights.transport.routes')}
          value={formatCount(data.routes, locale)}
          icon={<Icon name="switch" size={16} />}
          href={open(EduCorePaths.transportManagement)}
        />
        <C1Tile
          label={t('insights.transport.students')}
          value={formatCount(data.studentsServed, locale)}
          icon={<Icon name="users" size={16} />}
          href={open(EduCorePaths.transportOverview)}
        />
        <C1Tile
          label={t('insights.transport.assignments')}
          value={formatCount(data.assignments, locale)}
          icon={<Icon name="checklist" size={16} />}
          href={open(EduCorePaths.transportManagement)}
        />
        <C1Tile
          label={t('insights.transport.monthlyCost')}
          value={data.monthlyCost.toLocaleString(locale)}
          icon={<Icon name="report" size={16} />}
          href={open(EduCorePaths.transportManagement)}
        />
        {data.overBudget != null && data.overBudget > 0 && (
          <C1Tile
            label={t('insights.transport.overBudget')}
            value={formatCount(data.overBudget, locale)}
            icon={<Icon name="warning" size={16} />}
            tone="warning"
            href={open(EduCorePaths.transportManagement)}
          />
        )}
      </div>

      <div className="insight-charts">
        <CategoryBars
          title={t('insights.transport.byRoute')}
          data={data.byRoute.map((entry) => ({
            key: entry.route,
            label: entry.route,
            value: entry.count,
          }))}
          emptyMessage={t('insights.transport.noRoutes')}
        />
        <C1Donut
          title={t('insights.transport.budget')}
          slices={data.statusMix.map((entry) => ({
            key: entry.status,
            label: entry.status,
            value: entry.count,
          }))}
          format={(n) => formatCount(n, locale)}
          centre={{
            value: formatCount(data.assignments, locale),
            label: t('insights.transport.assignments'),
          }}
        />
      </div>
    </C1Dashboard>
  );
}

/**
 * DEAN, HEAD OF DEPARTMENT, PRINCIPAL, DIRECTOR — the academic overview grew into its own
 * composition (executive command centre + legacy fallback) and lives under `./overview/`.
 * Re-exported here so InsightsPanel's routing keeps its one import site.
 */
export { AcademicOverviewPanel } from './overview/AcademicOverviewPanel';

